import React, { createContext, useContext, useReducer, useEffect } from 'react';

const CacheContext = createContext();

const cacheReducer = (state, action) => {
  switch (action.type) {
    case 'UPDATE_CACHE':
      // Deep merge the cache update
      return {
        ...state,
        user_quote_section_properties_cached: deepMerge(
          state.user_quote_section_properties_cached || {},
          action.payload
        )
      };
    case 'SET_CACHE':
      // Replace entire cache
      return {
        ...state,
        user_quote_section_properties_cached: action.payload
      };
    case 'CLEAR_CACHE':
      return {
        ...state,
        user_quote_section_properties_cached: {}
      };
    default:
      return state;
  }
};

// Deep merge helper function
const deepMerge = (target, source) => {
  const output = { ...target };
  
  Object.keys(source).forEach(key => {
    if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
      output[key] = deepMerge(target[key] || {}, source[key]);
    } else {
      output[key] = source[key];
    }
  });
  
  return output;
};

export const CacheProvider = ({ children, initialCache }) => {
  const [state, dispatch] = useReducer(cacheReducer, {
    user_quote_section_properties_cached: initialCache || window.user_quote_section_properties_cached || {}
  });

  // Sync with window object for backward compatibility
  useEffect(() => {
    window.user_quote_section_properties_cached = state.user_quote_section_properties_cached;
    
    // Dispatch custom event when cache updates
    window.dispatchEvent(new CustomEvent('cache-updated', { 
      detail: state.user_quote_section_properties_cached 
    }));
  }, [state.user_quote_section_properties_cached]);

  // Listen for external cache updates (for backward compatibility)
  useEffect(() => {
    const handleExternalUpdate = (event) => {
      if (event.detail && event.detail !== state.user_quote_section_properties_cached) {
        dispatch({ type: 'UPDATE_CACHE', payload: event.detail });
      }
    };

    window.addEventListener('external-cache-update', handleExternalUpdate);
    
    return () => {
      window.removeEventListener('external-cache-update', handleExternalUpdate);
    };
  }, [state.user_quote_section_properties_cached]);

  return (
    <CacheContext.Provider value={{ state, dispatch }}>
      {children}
    </CacheContext.Provider>
  );
};

export const useCache = () => {
  const context = useContext(CacheContext);
  if (!context) {
    // Fallback for components not wrapped in provider
    return {
      state: { 
        user_quote_section_properties_cached: window.user_quote_section_properties_cached || {} 
      },
      dispatch: (action) => {
        console.warn('CacheContext not available, falling back to window object');
        if (action.type === 'UPDATE_CACHE' || action.type === 'SET_CACHE') {
          window.user_quote_section_properties_cached = action.payload;
        }
      }
    };
  }
  return context;
};